import styled from "styled-components";
import type {
  Assets,
  TdsGenerateAssetJobSchema,
} from "../../../../../types/types.PIM";
import { SecondaryButtonSmallV2 } from "../../../../../components/Buttons/Buttons";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import type { AxiosError } from "axios";
import axios from "axios";
import { endpoints } from "../../../../../endpoints";
import { useStoreState } from "../../../../../util/util";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { spacingTokens } from "../../../../../designTokens";

const GenerateDocumentWrapper = styled.div`
  padding: 18px 25px ${({ theme }) => theme.resolve("spacing_2xl")};
  margin: 10px 0 30px !important;
  border: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.primaryBorder}`};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: 13px;
  background: ${({ theme }) => theme.primaryBG};
  min-height: 115px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const Link = styled.a`
  color: #0090e3;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
`;

const TIMEOUT_INTERVAL = 2000;
/** Match RQ job timeout budget; customized TDS polls pdfkit every 10s. */
const MAX_POLL_DURATION_MS = 10 * 60 * 1000;

const TERMINAL_FAILURE_STATUSES = new Set(["failed", "failure"]);
const TERMINAL_SUCCESS_STATUSES = new Set(["finished"]);

export const GenerateDocument = ({
  asset: existingAsset,
  generate_id,
  product_id,
  onGenerate,
  update_poll_timeout_id,
}: {
  asset?: Assets;
  generate_id: string;
  product_id: string;
  onGenerate: (asset: Assets, updatedProductId?: string) => void;
  update_poll_timeout_id: (timeout_id: NodeJS.Timeout) => void;
}) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { notifyError } = useNotifications();
  const [asset, setAsset] = useState<Assets | undefined>(existingAsset);
  const [isGenerating, setIsGenerating] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setAsset(existingAsset);
  }, [existingAsset]);

  const stopGenerating = () => {
    if (isMountedRef.current) {
      setIsGenerating(false);
    }
  };

  const generate = async () => {
    setIsGenerating(true);
    const pollStartedAt = Date.now();
    let timeout_id: NodeJS.Timeout | undefined;

    const clearPollTimeout = () => {
      if (timeout_id) {
        clearTimeout(timeout_id);
        timeout_id = undefined;
      }
    };

    const failGeneration = (message: string, error?: unknown) => {
      clearPollTimeout();
      stopGenerating();
      notifyError(message, error ? { error } : undefined);
    };

    const scheduleNextPoll = (polling: () => Promise<void>) => {
      timeout_id = setTimeout(() => {
        void polling();
      }, TIMEOUT_INTERVAL);
      update_poll_timeout_id(timeout_id);
    };

    try {
      await axios.post(
        endpoints.v2_tenants_tenant_id_products_product_id_generate_assets_id_async(
          tenant_id,
          product_id,
          generate_id
        ),
        null,
        {
          params: {
            return_invisible_asset: true,
            regenerate_product_asset: Boolean(existingAsset),
            category_id: existingAsset?.category.id,
          },
        }
      );

      const polling = async () => {
        try {
          if (Date.now() - pollStartedAt > MAX_POLL_DURATION_MS) {
            failGeneration(
              t(
                "Document generation timed out. Please try again later."
              )
            );
            return;
          }

          const { data } = await axios.get<TdsGenerateAssetJobSchema>(
            endpoints.v2_tenants_tenant_id_products_product_id_generate_assets_id_status(
              tenant_id,
              product_id,
              generate_id
            )
          );

          if (
            TERMINAL_SUCCESS_STATUSES.has(data.status) ||
            (data.status === "success" && data.tds_asset)
          ) {
            if (!data.tds_asset) {
              failGeneration(
                t("Could not generate asset. Please try again later.")
              );
              return;
            }
            clearPollTimeout();
            if (isMountedRef.current) {
              setAsset(data.tds_asset);
              onGenerate(data.tds_asset);
              setIsGenerating(false);
            }
            return;
          }

          if (TERMINAL_FAILURE_STATUSES.has(data.status)) {
            failGeneration(
              t("Could not generate asset. Please try again later.")
            );
            return;
          }

          scheduleNextPoll(polling);
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          failGeneration(
            errorMessage
              ? errorMessage
              : t("Could not generate asset. Please try again later."),
            error
          );
        }
      };

      scheduleNextPoll(polling);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      failGeneration(
        errorMessage
          ? errorMessage
          : t("Could not generate asset. Please try again later."),
        error
      );
    }
  };

  const generateText = asset
    ? t("Click to regenerate")
    : t("Click to generate");

  return (
    <GenerateDocumentWrapper>
      {asset && (
        <>
          <div>{t("Click to preview the file")}</div>
          <div>
            <Link
              href={asset.signed_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {asset.name}
            </Link>
          </div>
          <div>{t("Or")}</div>
        </>
      )}
      <SecondaryButtonSmallV2
        style={{ marginTop: spacingTokens.spacing_md }}
        type="button"
        loading={isGenerating}
        onClick={generate}
      >
        {generateText}
      </SecondaryButtonSmallV2>
    </GenerateDocumentWrapper>
  );
};
